import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import Breadcrumbs from "@/components/base/Breadcrumbs";
import SectionHeading from "@/components/base/SectionHeading";
import useSEO from "@/hooks/useSEO";
import FeedbackForm from "./components/FeedbackForm";
import { aboutImage, contentPrinciples, milestones, readerVoices, siteInfo, siteStats } from "@/mocks/site";

const origin = typeof window !== "undefined" ? window.location.origin : "";

export default function About() {
  const location = useLocation();

  useEffect(() => {
    if (location.hash === "#feedback") {
      const target = document.getElementById("feedback");
      if (target) {
        target.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }
  }, [location.hash]);

  useSEO({
    title: "关于本站 | 爆料专区",
    description:
      "了解爆料专区的品牌定位、内容原则、发展历程与联系方式。我们只做公开信息的整理与复盘，坚持匿名化叙述，把娱乐事件的来龙去脉讲清楚。",
    keywords: "爆料专区,关于本站,内容原则,年度盘点,旧瓜复盘,联系我们",
    canonical: "/about",
    ogType: "website",
    image: aboutImage,
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "AboutPage",
        name: "关于爆料专区",
        description: siteInfo.intro,
        url: `${origin}/about`,
        inLanguage: "zh-CN",
        isPartOf: { "@type": "WebSite", name: siteInfo.name, url: `${origin}/` },
      },
      {
        "@context": "https://schema.org",
        "@type": "Organization",
        name: siteInfo.name,
        url: `${origin}/`,
        email: siteInfo.email,
        foundingDate: siteInfo.since,
        description: siteInfo.intro,
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: `${origin}/` },
          { "@type": "ListItem", position: 2, name: "关于本站", item: `${origin}/about` },
        ],
      },
    ],
  });

  return (
    <div className="w-full">
      <section className="border-b border-background-200 bg-background-100">
        <div className="mx-auto w-full max-w-[1400px] px-4 py-8 md:px-6 md:py-12">
          <Breadcrumbs items={[{ label: "首页", to: "/" }, { label: "关于本站" }]} />
          <div className="mt-6 grid grid-cols-1 items-center gap-8 lg:grid-cols-2 lg:gap-14">
            <div>
              <p className="mb-2 flex items-center gap-2 text-xs font-medium tracking-[0.24em] text-primary-600">
                <span className="inline-block h-3 w-1 rounded-full bg-primary-500" />
                ABOUT US
              </p>
              <h1 className="font-heading text-2xl font-black leading-snug text-foreground-950 md:text-4xl">
                关于爆料专区
              </h1>
              <p className="mt-4 text-sm leading-relaxed text-foreground-600 md:text-base">
                {siteInfo.intro}
              </p>
              <div className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-3 text-sm text-foreground-600">
                <span className="flex items-center gap-2">
                  <i className="ri-time-line text-primary-500" />
                  始于 {siteInfo.since} 年
                </span>
                <span className="flex items-center gap-2">
                  <i className="ri-map-pin-line text-primary-500" />
                  {siteInfo.location}
                </span>
                <a
                  href={`mailto:${siteInfo.email}`}
                  className="flex items-center gap-2 text-foreground-700 transition-colors hover:text-primary-600"
                >
                  <i className="ri-mail-line text-primary-500" />
                  {siteInfo.email}
                </a>
              </div>
            </div>
            <figure className="overflow-hidden rounded-lg border border-background-200">
              <div className="h-52 w-full bg-secondary-100 md:h-72">
                <img
                  src={aboutImage}
                  alt="爆料专区编辑部工作台，打字机与档案夹的温暖场景"
                  title="爆料专区 · 关于我们"
                  className="h-full w-full object-cover object-top"
                />
              </div>
            </figure>
          </div>
        </div>
      </section>

      <section className="w-full">
        <div className="mx-auto w-full max-w-[1400px] px-4 py-12 md:px-6 md:py-16">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-[1fr_340px] lg:gap-14">
            <div>
              <SectionHeading eyebrow="POSITIONING" title="我们想做的事" />
              <div className="mt-6 space-y-5 text-base leading-[1.95] text-foreground-700">
                <p>
                  娱乐话题更新得太快。一个事件从发酵到平息，往往只需要几天，留下的是满天飞的碎片信息：有人记得开头，有人只看到结尾，还有人把两件不相干的事混在了一起。
                </p>
                <p>
                  爆料专区想做的，是慢一点。我们把已经公开的信息收集起来，按主题与年份归档，补上时间线与各方回应，让一件旧事也能被完整地重新读懂。不追热点，只做整理。
                </p>
                <p>
                  这里的内容面向两类读者：想重温的老观众，和想补课的新读者。无论你是哪一类，都希望你能看完之后，对事情的来龙去脉心里有数。
                </p>
              </div>
              <Link
                to="/archive"
                className="mt-7 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-foreground-950 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-foreground-800"
              >
                <i className="ri-archive-2-line" />
                去档案库看看
              </Link>
            </div>

            <dl className="grid h-fit grid-cols-2 gap-4 rounded-lg border border-background-200 bg-background-100 p-5">
              {siteStats.map((stat) => (
                <div key={stat.label} className="rounded-md bg-background-50 p-4">
                  <dt className="text-xs text-foreground-500">{stat.label}</dt>
                  <dd className="mt-1.5 font-heading text-2xl font-black text-primary-600">
                    {stat.value}
                  </dd>
                </div>
              ))}
            </dl>
          </div>
        </div>
      </section>

      <section className="w-full bg-background-100">
        <div className="mx-auto w-full max-w-[1400px] px-4 py-12 md:px-6 md:py-16">
          <SectionHeading
            eyebrow="PRINCIPLES"
            title="四条内容原则"
            description="这些规则写在编辑部的工作手册里，也是每一篇档案上线前必须过的关卡。"
          />
          <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2">
            {contentPrinciples.map((principle, index) => (
              <div
                key={principle.title}
                className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6"
              >
                <span className="flex h-10 w-10 items-center justify-center rounded-md bg-accent-100 font-heading text-base font-bold text-accent-900">
                  {String(index + 1).padStart(2, "0")}
                </span>
                <h3 className="mt-4 font-heading text-lg font-bold text-foreground-950">
                  {principle.title}
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-foreground-600">{principle.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="w-full">
        <div className="mx-auto w-full max-w-[1400px] px-4 py-12 md:px-6 md:py-16">
          <SectionHeading
            eyebrow="HISTORY"
            title="发展历程"
            description="从零散整理到体系化归档，这条路我们走了几年，也还在继续。"
          />
          <ol className="mt-8 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
            {milestones.map((item) => (
              <li key={item.year} className="relative rounded-lg border border-background-200 bg-background-100 p-5">
                <span className="font-heading text-2xl font-black text-primary-500">{item.year}</span>
                <h3 className="mt-3 font-heading text-base font-bold text-foreground-950">
                  {item.title}
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-foreground-600">{item.desc}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <section className="w-full bg-secondary-100">
        <div className="mx-auto w-full max-w-[1400px] px-4 py-12 md:px-6 md:py-16">
          <SectionHeading
            eyebrow="VOICES"
            title="读者口碑"
            description="这些反馈来自长期关注我们的读者，也是我们坚持克制的理由。"
          />
          <div className="mt-8 grid grid-cols-1 gap-5 md:grid-cols-3">
            {readerVoices.slice(3, 6).map((voice) => (
              <figure key={voice.name} className="flex h-full flex-col rounded-lg bg-background-50 p-5 md:p-6">
                <i className="ri-double-quotes-l text-2xl text-secondary-500" />
                <blockquote className="mt-3 flex-1 text-sm leading-relaxed text-foreground-700">
                  {voice.text}
                </blockquote>
                <figcaption className="mt-5 flex items-center gap-3 border-t border-background-200 pt-4">
                  <span className="flex h-9 w-9 items-center justify-center rounded-full bg-primary-100 font-heading text-sm font-bold text-primary-700">
                    {voice.name.slice(0, 1)}
                  </span>
                  <span>
                    <span className="block text-sm font-medium text-foreground-900">{voice.name}</span>
                    <span className="block text-xs text-foreground-500">{voice.role}</span>
                  </span>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      <section id="feedback" className="w-full scroll-mt-24">
        <div className="mx-auto w-full max-w-[1400px] px-4 py-12 md:px-6 md:py-16">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-[360px_1fr] lg:gap-14">
            <div>
              <SectionHeading
                eyebrow="CONTACT"
                title="联系编辑部"
                description="发现内容有误、想补充线索，或者希望我们整理某个事件，都可以通过这张表告诉我们。我们会逐条查看。"
              />
              <div className="mt-6 space-y-3 text-sm">
                <a
                  href={`mailto:${siteInfo.email}`}
                  className="flex items-center gap-2 text-foreground-700 transition-colors hover:text-primary-600"
                >
                  <i className="ri-mail-line text-primary-500" />
                  邮箱：{siteInfo.email}
                </a>
                <p className="flex items-center gap-2 text-foreground-600">
                  <i className="ri-time-line text-primary-500" />
                  回复时间：通常在 3 个工作日内
                </p>
                <p className="flex items-start gap-2 text-foreground-600">
                  <i className="ri-information-line mt-0.5 text-primary-500" />
                  请尽量附上公开来源，方便我们核实后更新档案。
                </p>
              </div>
            </div>
            <FeedbackForm />
          </div>
        </div>
      </section>
    </div>
  );
}